
import { defineComponent, PropType } from 'vue';
import BaseIcon from '@/components/BaseIcon.vue';

type ValueType = string | number | boolean;

interface Option {
  text: string;
  value: ValueType;
  disabled?: boolean;
}

export default defineComponent({
  components: {
    BaseIcon,
  },
  props: {
    prop_options: {
      type: Array as PropType<Option[]>,
      default: () => [],
    },
    selectedValue: {
      type: [String, Number, Boolean, Array] as PropType<
        ValueType | ValueType[]
      >,
      default: '',
    },
    placeholder: {
      type: String,
      default: 'Selecione uma opção',
    },
    multiple: {
      type: Boolean,
      default: false,
    },
    limit: {
      type: Number,
      default: 0,
    },
    empFilConsolidado: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    options(): Option[] {
      return this.prop_options.map((option) => ({
        text: option.text,
        value: option.value,
        disabled: option.disabled,
      }));
    },
    displaySelectedText(): string {
      if (this.multiple) {
        const values = Array.isArray(this.dropdownValue)
          ? this.dropdownValue
          : [];
        if (values.length === 0) return '';
        return values
          .map((val) => {
            const foundOption = this.options.find((opt) => opt.value === val);
            return foundOption ? foundOption.text : '';
          })
          .filter((text) => text !== '')
          .join(', ');
      } else {
        return (
          this.options.find((option) => option.value === this.dropdownValue)
            ?.text || ''
        );
      }
    },
  },
  data(): { showDropdown: boolean; dropdownValue: ValueType | ValueType[] } {
    return {
      showDropdown: false,
      dropdownValue: this.multiple
        ? this.parseMultiValues(this.selectedValue)
        : this.selectedValue,
    };
  },
  methods: {
    toggleDropdown() {
      this.showDropdown = !this.showDropdown;
    },
    isSelected(value: ValueType): boolean {
      if (this.multiple) {
        return (
          Array.isArray(this.dropdownValue) &&
          this.dropdownValue.includes(value)
        );
      }
      return this.dropdownValue === value;
    },
    isLimitReached(value: ValueType): boolean {
      if (!this.multiple || this.limit <= 0) return false;
      const isCurrentlySelected =
        Array.isArray(this.dropdownValue) && this.dropdownValue.includes(value);
      const limitReached =
        Array.isArray(this.dropdownValue) &&
        this.dropdownValue.length >= this.limit;
      return limitReached && !isCurrentlySelected;
    },
    selectOption(option: Option) {
      if (this.multiple) {
        let currentValues = Array.isArray(this.dropdownValue)
          ? [...this.dropdownValue]
          : [];
        const index = currentValues.indexOf(option.value);
        if (index === -1) {
          if (this.limit === 0 || currentValues.length < this.limit) {
            currentValues.push(option.value);
          } else {
            return;
          }
        } else {
          currentValues.splice(index, 1);
        }
        this.dropdownValue = currentValues;
        let emitValue: any = '';
        if (currentValues.length > 0) {
          if (this.empFilConsolidado) {
            const cleanEmits = currentValues.map((v) =>
              String(v).replace(/#/g, '')
            );
            emitValue = `#${cleanEmits.join('#')}#`;
          } else {
            emitValue = currentValues.join(',');
          }
        }
        this.$emit('select-option', emitValue);
      } else {
        this.dropdownValue = option.value;
        this.$emit('select-option', option.value);
        this.toggleDropdown();
      }
    },
    parseMultiValues(val: ValueType | ValueType[]): ValueType[] {
      if (Array.isArray(val)) return [...val];
      if (typeof val === 'string' && val.trim() !== '') {
        if (this.empFilConsolidado && val.includes('#')) {
          return val
            .split('#')
            .filter((p) => p.trim() !== '')
            .map((p) => `#${p}#`);
        } else if (!this.empFilConsolidado && val.includes(',')) {
          return val
            .split(',')
            .map((p) => p.trim())
            .filter((p) => p !== '');
        }
        return [val];
      }
      return val ? [val] : [];
    },
  },
  watch: {
    selectedValue(newVal: ValueType | ValueType[]) {
      if (this.multiple) {
        this.dropdownValue = this.parseMultiValues(newVal);
      } else {
        this.dropdownValue = newVal;
      }
    },
  },
});
